import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Recycle, Info, Coins } from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const TRADE_IMG = "https://images.unsplash.com/photo-1579871295656-28249508ee6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function TradeRice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [range, setRange] = useState("10-15");
  const [form, setForm] = useState({ pickup_date: "", pickup_time: "10 AM - 12 PM", address: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/tradein/config").then(({ data }) => setConfig(data)); }, []);

  const mid = config?.ranges.find((r) => r.key === range)?.mid || 0;
  const ppk = config?.ricepoints_per_kg || 10;
  const est = Math.round(mid * ppk);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tradein", {
        quantity_range: range, estimated_kg: mid, ...form,
      });
      toast.success(`Trade-in ${data.trade_id} created!`);
      navigate(`/trade/${data.trade_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-primary/10 text-primary px-3 py-1.5 rounded-full"><Recycle size={14} /> Trade Your Rice</span>
          <h1 className="mt-4 font-heading font-extrabold text-4xl tracking-tight">Have rice at home you no longer use?</h1>
          <p className="mt-3 text-muted-foreground">Trade eligible household rice with Avaginja and receive RicePoints toward your next purchase.</p>
          <img src={TRADE_IMG} alt="Rice sacks" className="mt-6 rounded-xl border border-border w-full max-h-72 object-cover" />
          <div className="mt-6 bg-secondary text-secondary-foreground rounded-xl p-5 flex items-start gap-3">
            <Info size={18} className="mt-0.5 shrink-0" />
            <p className="text-sm">Final RicePoints are calculated after physical weighing and acceptance. We don't guarantee points before inspection.</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-card border border-border rounded-xl p-6" data-testid="tradein-form">
          <h2 className="font-heading font-bold text-xl">Request a pickup</h2>

          <Label className="mt-5 block">Approximate quantity</Label>
          <RadioGroup value={range} onValueChange={setRange} className="mt-2 grid grid-cols-2 gap-2">
            {config?.ranges.map((r) => (
              <label key={r.key} className={`flex items-center gap-2 border rounded-md p-2.5 cursor-pointer text-sm ${range === r.key ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`range-${r.key}`}>
                <RadioGroupItem value={r.key} /> {r.label}
              </label>
            ))}
          </RadioGroup>

          <div className="mt-4 bg-primary/10 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm"><Coins className="text-primary" size={18} /> Estimated reward</div>
            <div className="font-heading font-extrabold text-2xl text-primary font-mono-points" data-testid="tradein-estimate">{money(est)}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">~{mid} kg × ₹{ppk} per kg</div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div><Label>Pickup date</Label><Input type="date" required value={form.pickup_date} onChange={set("pickup_date")} data-testid="tradein-date" className="mt-1.5" /></div>
            <div>
              <Label>Pickup time</Label>
              <select value={form.pickup_time} onChange={set("pickup_time")} data-testid="tradein-time" className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                {["10 AM - 12 PM", "12 PM - 2 PM", "2 PM - 4 PM", "4 PM - 6 PM"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3"><Label>Pickup address</Label><Textarea required rows={2} value={form.address} onChange={set("address")} data-testid="tradein-address" className="mt-1.5" placeholder="House, street, area, Hyderabad" /></div>
          <div className="mt-3"><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="tradein-notes" className="mt-1.5" placeholder="Type of rice, condition…" /></div>

          <Button type="submit" size="lg" className="w-full mt-5" disabled={busy} data-testid="tradein-submit">
            {busy ? "Submitting…" : user ? "Request Pickup" : "Login to Request"}
          </Button>
        </form>
      </div>
    </div>
  );
}
